import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, DollarSign, User, AlertTriangle, ArrowLeft, 
  RefreshCw, Printer, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RefundCautionMoney() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentStatus, setStudentStatus] = useState(null);
  const [banks, setBanks] = useState([]);
  const [actionType, setActionType] = useState('refund'); // refund or forfeit
  const [formData, setFormData] = useState({
    amount: '',
    mode: 'Cash',
    bank_id: '',
    bank_name: '',
    transaction_ref: '',
    reason: 'Course Completion',
    remarks: '',
    refund_date: new Date().toISOString().split('T')[0]
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchBanks();
    const studentId = searchParams.get('student');
    if (studentId) {
      loadStudent(studentId);
    }
  }, []);

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bank-register/banks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanks(data || []);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const loadStudent = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const [studentRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/students/${studentId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/caution-money/student/${studentId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (studentRes.ok && statusRes.ok) {
        const student = await studentRes.json();
        const status = await statusRes.json();
        setSelectedStudent(student);
        setStudentStatus(status);
        setFormData(prev => ({ ...prev, amount: status.current_balance }));
      }
    } catch (error) {
      console.error('Error loading student:', error);
    }
  };

  const searchStudents = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/students/?search=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.slice(0, 10));
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setStudents([]);
    setSearchQuery('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caution-money/student/${student.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStudentStatus(data);
        setFormData(prev => ({ ...prev, amount: data.current_balance }));
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent || !formData.amount) {
      toast.error('Please select student and enter amount');
      return;
    }

    if (parseFloat(formData.amount) > studentStatus?.current_balance) {
      toast.error('Amount exceeds available balance');
      return;
    }

    if (actionType === 'refund' && formData.mode !== 'Cash' && !formData.bank_id) {
      toast.error('Please select bank for non-cash refund');
      return;
    }

    if (actionType === 'forfeit' && !formData.reason.trim()) {
      toast.error('Please enter reason for forfeiture');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = actionType === 'refund' ? '/api/caution-money/refund' : '/api/caution-money/forfeit';
      
      const body = actionType === 'refund' ? {
        student_id: selectedStudent.id,
        refund_amount: parseFloat(formData.amount),
        refund_mode: formData.mode,
        bank_id: formData.bank_id || null,
        bank_name: formData.bank_name || null,
        transaction_ref: formData.transaction_ref || null,
        reason: formData.reason,
        remarks: formData.remarks || null,
        refund_date: formData.refund_date || null
      } : {
        student_id: selectedStudent.id,
        forfeit_amount: parseFloat(formData.amount),
        reason: formData.reason,
        remarks: formData.remarks || null,
        forfeit_date: formData.refund_date || null
      };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setResult({
          ...data,
          student: selectedStudent,
          amount: parseFloat(formData.amount),
          type: actionType,
          date: formatDate()
        });
      } else {
        toast.error(data.detail || 'Operation failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to process');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setStudentStatus(null);
    setResult(null);
    setFormData({
      amount: '',
      mode: 'Cash',
      bank_id: '',
      bank_name: '',
      transaction_ref: '',
      reason: 'Course Completion',
      remarks: ''
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Refund / Forfeit Caution Money</h1>
          <p className="text-gray-600 mt-1">Process refund or forfeit security deposit</p>
        </div>
      </div>

      {!result ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Select Student */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Select Student
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchStudents()}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Search student..."
                  />
                </div>
                <Button onClick={searchStudents}>Search</Button>
              </div>

              {students.length > 0 && (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      onClick={() => selectStudent(student)}
                      className="p-3 hover:bg-blue-50 cursor-pointer"
                    >
                      <p className="font-medium">{student.full_name}</p>
                      <p className="text-sm text-gray-500">{student.admission_number}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedStudent && studentStatus && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-lg">{selectedStudent.full_name}</p>
                      <p className="text-sm text-gray-600">{selectedStudent.admission_number}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={resetForm}>Change</Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-gray-500">Paid</p>
                      <p className="font-semibold text-blue-600">₹{studentStatus.total_paid?.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-gray-500">Refunded</p>
                      <p className="font-semibold text-green-600">₹{studentStatus.total_refunded?.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-gray-500">Balance</p>
                      <p className="font-semibold text-amber-600">₹{studentStatus.current_balance?.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {studentStatus.current_balance <= 0 && (
                    <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-red-700 text-sm">
                      No balance available for refund/forfeit
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right - Action Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Process Action
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Action Type */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={actionType === 'refund' ? 'default' : 'outline'}
                    onClick={() => setActionType('refund')}
                    className={`flex-1 ${actionType === 'refund' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    Refund
                  </Button>
                  <Button
                    type="button"
                    variant={actionType === 'forfeit' ? 'default' : 'outline'}
                    onClick={() => setActionType('forfeit')}
                    className={`flex-1 ${actionType === 'forfeit' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  >
                    Forfeit
                  </Button>
                </div>

                {actionType === 'forfeit' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">Warning: Forfeited amount will be recorded as income</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{actionType === 'refund' ? 'Refund Date' : 'Forfeit Date'}</label>
                  <input type="date" value={formData.refund_date} onChange={(e) => setFormData(prev => ({ ...prev, refund_date: e.target.value }))} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-gray-300 rounded-lg" data-testid="caution-refund-date" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    max={studentStatus?.current_balance || 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Max: ₹{studentStatus?.current_balance?.toLocaleString('en-IN') || 0}</p>
                </div>

                {actionType === 'refund' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Refund Mode</label>
                      <select
                        value={formData.mode}
                        onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Cash">Cash</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="UPI">UPI</option>
                        <option value="Cheque">Cheque</option>
                      </select>
                    </div>

                    {formData.mode !== 'Cash' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                          <select
                            value={formData.bank_id}
                            onChange={(e) => {
                              const bank = banks.find(b => b.id === e.target.value);
                              setFormData(prev => ({
                                ...prev,
                                bank_id: e.target.value,
                                bank_name: bank?.bank_name || ''
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            required
                          >
                            <option value="">Select Bank</option>
                            {banks.map((bank) => (
                              <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                          <input
                            type="text"
                            value={formData.transaction_ref}
                            onChange={(e) => setFormData(prev => ({ ...prev, transaction_ref: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="UTR / Cheque No"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason {actionType === 'forfeit' && <span className="text-red-500">*</span>}
                  </label>
                  {actionType === 'refund' ? (
                    <select
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Course Completion">Course Completion</option>
                      <option value="Transfer Certificate">Transfer Certificate</option>
                      <option value="Course Withdrawal">Course Withdrawal</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., Library book damage, Lab equipment damage"
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="2"
                  />
                </div>

                <Button 
                  type="submit" 
                  className={`w-full ${actionType === 'refund' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                  disabled={loading || !selectedStudent || studentStatus?.current_balance <= 0}
                >
                  {loading ? 'Processing...' : actionType === 'refund' ? 'Process Refund' : 'Process Forfeiture'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Result */
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                result.type === 'refund' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <CheckCircle className={`h-8 w-8 ${result.type === 'refund' ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <h2 className="text-2xl font-bold mt-4">
                {result.type === 'refund' ? 'Refund Processed' : 'Forfeiture Recorded'}
              </h2>
              <p className="text-gray-600">{result.receipt_no || result.entry_no}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-500">Student</p>
              <p className="font-semibold">{result.student?.full_name}</p>
            </div>

            <div className={`border-2 rounded-lg p-4 mb-6 text-center ${
              result.type === 'refund' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
            }`}>
              <p className="text-sm text-gray-500">Amount {result.type === 'refund' ? 'Refunded' : 'Forfeited'}</p>
              <p className={`text-3xl font-bold ${result.type === 'refund' ? 'text-green-600' : 'text-red-600'}`}>
                ₹{result.amount?.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p><strong>Remaining Balance:</strong> ₹{result.remaining_balance?.toLocaleString('en-IN')}</p>
              {result.reason && <p><strong>Reason:</strong> {result.reason}</p>}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => window.print()} className="flex-1">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
              <Button variant="outline" onClick={resetForm} className="flex-1">
                Process Another
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
